/**
 * License Usage Banner Component
 * Simple, clean display of license usage for multi-tenant users
 */

import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Shield, 
  Zap, 
  Crown,
  Users,
  UserCog
} from 'lucide-react';

// Edition configurations
const EDITION_CONFIG = {
  standard: {
    label: 'Standard',
    color: 'bg-slate-500',
    icon: Shield,
  },
  professional: {
    label: 'Professional',
    color: 'bg-blue-500',
    icon: Zap,
  },
  enterprise: {
    label: 'Enterprise',
    color: 'bg-purple-500',
    icon: Crown,
  },
};

export const LicenseUsageBanner = ({ licenseUsage }) => {
  if (!licenseUsage) return null;

  const { license, edition } = licenseUsage;
  const editionConfig = EDITION_CONFIG[edition] || EDITION_CONFIG.standard;
  const EditionIcon = editionConfig.icon;

  const creatorUsed = license?.active_creators || 0;
  const creatorTotal = license?.creator_licenses || 0;
  const viewerUsed = license?.active_viewers || 0;
  const viewerTotal = license?.viewer_licenses || 0;

  // Calculate percentages for progress bars
  const creatorPercent = creatorTotal > 0 ? Math.min(100, (creatorUsed / creatorTotal) * 100) : 0;
  const viewerPercent = viewerTotal > 0 ? Math.min(100, (viewerUsed / viewerTotal) * 100) : 0;

  return (
    <Card className="border-muted" data-testid="license-usage-banner">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-6">
          {/* Edition Info */}
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${editionConfig.color} flex items-center justify-center`}>
              <EditionIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <Badge className={`${editionConfig.color} text-white text-xs`} data-testid="edition-badge">
                {editionConfig.label}
              </Badge>
            </div>
          </div>

          {/* License Counters */}
          <div className="flex items-center gap-6 flex-1 justify-end">
            {/* Creators */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <UserCog className="w-4 h-4" />
                <span className="text-xs font-medium">Creators</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${creatorPercent >= 100 ? 'bg-red-500' : 'bg-emerald-500'}`}
                    style={{ width: `${creatorPercent}%` }}
                  />
                </div>
                <span className={`text-sm font-semibold min-w-[50px] ${creatorPercent >= 100 ? 'text-red-500' : ''}`}>
                  {creatorUsed}/{creatorTotal}
                </span>
              </div>
            </div>

            {/* Viewers */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Users className="w-4 h-4" />
                <span className="text-xs font-medium">Viewers</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${viewerPercent >= 100 ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ width: `${viewerPercent}%` }}
                  />
                </div>
                <span className={`text-sm font-semibold min-w-[50px] ${viewerPercent >= 100 ? 'text-red-500' : ''}`}>
                  {viewerUsed}/{viewerTotal}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LicenseUsageBanner;
